import type { AppLeaf, CategoryNode, Selection, TreemapData } from "./types";

/** Keep the top N apps by current install count. */
const TOP_N = 100;

/** Colour mode: category (adoption) vs momentum (MoM). */
export type Mode = "cat" | "mom";

// Momentum colour keys off the SAME measure shown on the block: relative MoM %.
// Flat band = within ±1% MoM; beyond that green/red, deeper past ±5%.
// momRel == null => the app had no installs in the cohort last month (new entrant).
export const NEW_ENTRANT_COLOR = "#2f6fed";
export const FLAT_COLOR = "#c7ccd6";

export function momColor(v: number | null): string {
  if (v == null) return NEW_ENTRANT_COLOR;
  if (v > 1) return v > 5 ? "#1f9d4d" : "#2c7a47";
  if (v < -1) return v < -5 ? "#c0392b" : "#8e3b34";
  return FLAT_COLOR;
}

export function fmtRel(v: number | null): string {
  return v == null ? "New" : (v > 0 ? "+" : "") + v.toFixed(1) + "%";
}

/** Growth-tier membership for a selection (25% includes the 10%). */
function tierMatch(sel: string, tier: string): boolean {
  if (sel === "all") return true;
  if (sel === "top25") return tier === "top10" || tier === "top25";
  if (sel === "top10") return tier === "top10";
  return false;
}

/**
 * Aggregate the cube for a selection. Stable cohort => same denominator (the
 * segment total) for the current and previous month, so MoM is computed per
 * cohort from each app's cur/prev share within the selection.
 */
function aggregate(data: TreemapData, sel: Selection) {
  let total = 0;
  for (const [b, t, i, n] of data.segTotals) {
    if (sel.gmv !== "all" && b !== sel.gmv) continue;
    if (!tierMatch(sel.growth, t)) continue;
    if (sel.ind !== "all" && i !== sel.ind) continue;
    total += n;
  }
  const curMap = new Map<number, number>();
  const prevMap = new Map<number, number>();
  for (const [b, t, i, aid, cc, cp] of data.cells) {
    if (sel.gmv !== "all" && b !== sel.gmv) continue;
    if (!tierMatch(sel.growth, t)) continue;
    if (sel.ind !== "all" && i !== sel.ind) continue;
    curMap.set(aid, (curMap.get(aid) ?? 0) + cc);
    prevMap.set(aid, (prevMap.get(aid) ?? 0) + cp);
  }
  return { total, curMap, prevMap };
}

/**
 * Build the ordered category → app-leaf hierarchy for a selection. Returns the
 * cohort total (brand count) and the categories present, each with its apps
 * ranked into the shared top-N.
 */
export function buildHierarchy(
  data: TreemapData,
  sel: Selection
): { total: number; cats: CategoryNode[] } {
  const { total, curMap, prevMap } = aggregate(data, sel);

  const ranked = [...curMap.entries()]
    .map(([aid, cnt]) => ({ aid, cnt }))
    .toSorted((a, b) => b.cnt - a.cnt)
    .slice(0, TOP_N);

  const byCat: Record<string, AppLeaf[]> = {};
  for (const { aid, cnt } of ranked) {
    const app = data.apps[aid];
    if (!app) continue;
    const prevCnt = prevMap.get(aid) ?? 0;
    const pct = total ? (100 * cnt) / total : 0;
    const pctPrev = total ? (100 * prevCnt) / total : 0;
    const momPp = +(pct - pctPrev).toFixed(2);
    const momRel =
      pctPrev > 0 ? +(((pct - pctPrev) / pctPrev) * 100).toFixed(1) : null;
    (byCat[app.cat] ??= []).push({ name: app.name, value: pct, momPp, momRel });
  }

  const cats = data.categoryOrder
    .filter((c) => byCat[c])
    .map((c) => ({
      name: c,
      color: data.categoryColor[c] ?? FLAT_COLOR,
      children: byCat[c] ?? [],
    }));

  return { total, cats };
}

/** Total brands for an arbitrary selection (used to disable sub-minSample combos). */
export function totalFor(data: TreemapData, sel: Selection): number {
  let t = 0;
  for (const [b, tier, i, n] of data.segTotals) {
    if (sel.gmv !== "all" && b !== sel.gmv) continue;
    if (!tierMatch(sel.growth, tier)) continue;
    if (sel.ind !== "all" && i !== sel.ind) continue;
    t += n;
  }
  return t;
}
